













































































































import {
  Component, Prop, PropSync, Watch,
} from 'vue-property-decorator';
import { DetailedUserData } from '@/store/users/types';
import { PhoneInfo } from '@/components/IntegrityPhoneNumberInput/types';
import { OrganizationData } from '@/store/organizations/types';
import { SYSTEM_ADMIN_GUID } from '@/auth/roles';
import { FilterFunction } from '@/components/IntegrityTable/IntegrityTable.vue';
import { REDZONE_ORGANIZATION_GUID } from '@/common/Constants';
import hasRedzoneEmail from '@/common/utils/UserUtils';
import NewUserPopup from '../NewUserPopup.vue';
import IntegrityPhoneNumberInput from '../../IntegrityPhoneNumberInput/IntegrityPhoneNumberInput.vue';
import AdminPanelMixin from '../AdminPanelMixin.vue';

const defaultOrganization = {
  guid: undefined,
  organizationName: '',
  address: '',
  systemAdmin: null,
  email: '',
  phoneNumber: {
    phone: '',
    phoneCode: '+1',
    isInternational: false,
  },
};

@Component({
  components: {
    NewUserPopup,
    IntegrityPhoneNumberInput,
  },
})
export default class OrganizationEditDialog extends AdminPanelMixin {
  @PropSync('show') showDialog: boolean;

  @Prop() saveOrganizationData;

  @Prop({ default: false }) organizationLoading: boolean;

  @Prop() title: string;

  @Prop({
    default: () => ({ ...defaultOrganization }),
  })
  editItem: {
    guid: string | undefined;
    organizationName: string;
    address: string;
    systemAdmin: DetailedUserData | undefined;
    email: string;
    phoneNumber: PhoneInfo | undefined;
  };

  valid = false as boolean;

  systemAdminGuid = SYSTEM_ADMIN_GUID as string;

  confirmDialog = false;

  pendingSystemAdmin: DetailedUserData | null = null;

  previousSystemAdmin: DetailedUserData | null = null;

  // Vue 2 / Vuetify rule

  sysAdminRules = [
    (v: DetailedUserData | null | undefined): string | boolean => {
      if (hasRedzoneEmail(v?.email)) {
        return 'RedZone org users cannot be System Admins for outside orgs';
      }
      if (v?.organizationGuids?.some((guid) => guid === REDZONE_ORGANIZATION_GUID)) {
        return 'RedZone org users cannot be System Admins for outside orgs';
      }
      return true;
    },
  ];

  sysAdminEmailRules = [
    (email: string | null | undefined): string | boolean => {
      if (hasRedzoneEmail(email)) {
        return 'RedZone org users cannot be System Admins for outside orgs';
      }

      return true;
    },
  ];

  get filterFunctions(): FilterFunction[] {
    return [
      {
        functionVariables: [],
        filterFunction: function filterOutSysAdminsAss(item: DetailedUserData): boolean {
          return item.role !== SYSTEM_ADMIN_GUID;
        },
      },
      {
        functionVariables: [this.organizationDataList],
        filterFunction: function filterOutSysAdminsOrg(item: DetailedUserData,
          orgs: OrganizationData[]): boolean {
          return !orgs.some((org) => org.systemAdminGuid === item.guid);
        },
      },
      {
        functionVariables: [],
        filterFunction: function filterOutRedZoneOrgUsers(item: DetailedUserData): boolean {
          return !item.organizationGuids.some((orgGuid) => orgGuid === REDZONE_ORGANIZATION_GUID);
        },
      },
    ];
  }

  resetConfirmationDialogState(): void {
    this.previousSystemAdmin = this.editItem.systemAdmin;
    this.pendingSystemAdmin = null;
    this.confirmDialog = false;
  }

  cancel(): void {
    (this.$refs.organizationForm as HTMLFormElement).resetValidation();
    this.showDialog = false;
    this.resetConfirmationDialogState();
  }

  getOrganizationName(guid: string): string {
    const organization = this.organizationDataList.find((org) => org.guid === guid);
    return organization ? organization.name : 'Unknown Organization';
  }

  @Watch('editItem.systemAdmin', { immediate: false })
  onSystemAdminChange(newSystemAdmin: DetailedUserData, oldSystemAdmin: DetailedUserData): void {
    if (newSystemAdmin) {
      this.previousSystemAdmin = oldSystemAdmin;
      this.checkSystemAdmin(newSystemAdmin);
    }
  }

  @Watch('editItem', { immediate: false, deep: false })
  onEditItemChange(): void {
    this.resetConfirmationDialogState();
  }

  // Check if part of another organization. If so, prompt user to confirm change.
  checkSystemAdmin(newSystemAdmin: DetailedUserData): void {
    const otherOrganizations = newSystemAdmin.organizationGuids.filter(
      (org) => org !== this.editItem.guid,
    );

    if (otherOrganizations.length > 0) {
      this.pendingSystemAdmin = newSystemAdmin;
      this.confirmDialog = true;
    } else {
      this.editItem.systemAdmin = newSystemAdmin;
    }
  }

  confirmChange(): void {
    this.editItem.systemAdmin = this.pendingSystemAdmin;
    this.confirmDialog = false;
  }

  cancelChange(): void {
    this.editItem.systemAdmin = this.previousSystemAdmin;
    this.resetConfirmationDialogState();
  }

  cancelDialog(): void {
    this.resetConfirmationDialogState();
  }

  get isPendingChange(): boolean {
    return this.pendingSystemAdmin !== null;
  }

  /**
   * @description Calls endpoint to post or patch an organization and fetches new organization data.
   */
  async saveOrganization(): Promise<void> {
    const phoneCode = this.editItem.phoneNumber?.isInternational
      ? `${this.editItem.phoneNumber.phoneCode} `
      : '';
    const phone = this.editItem.phoneNumber?.phone
      ? `${this.editItem.phoneNumber.phone}`
      : '';

    const organizationSaveObject: OrganizationData = {
      guid: this.editItem.guid,
      name: this.editItem.organizationName,
      address: this.editItem.address,
      systemAdminGuid: this.editItem.systemAdmin?.guid,
      email: this.editItem.email,
      phoneNumber: `${phoneCode}${phone}`,
    };

    await this.saveOrganizationData(organizationSaveObject);

    const fetches = [
      this.fetchOrganizationData(),
      this.fetchCustomerData(),
      this.fetchAllDetailedUserData(),
    ];
    await Promise.all(fetches);
    this.editItem = { ...defaultOrganization };
    // No need to await this update for all users
    this.updateAllManagedUsers();
    this.$emit('organizationSaved', organizationSaveObject);
    this.showDialog = false;
    this.resetConfirmationDialogState();
  }
}
